import { useState, useEffect } from "react";
import "./LearningPlan.scss";
import { useGetGroups } from "../../../../../hooks/useGetGroups";
import { useSELStories, SELStory } from "../../../../../hooks/useSELStories";
import apiClient from "../../../../../api/apiClient";
import apiPaths from "../../../../../api/endpoints";
import { toast } from "react-toastify";
import StoriesProgress from "../../../../../components/Insights/StoriesSnapShot/StoriesProgress";
import { trackEvent } from "../../../../../mixpanel";

type Theme = {
  name: string;
  color: string;
  stories: { title: string }[];
};

type Suggestion = {
  text: string;
  type: "negative" | "warning" | "positive";
};

interface LearningPlanProps {
  schoolHash: string;
  grade: string;
  classroomData: any;
}

const LearningPlan: React.FC<LearningPlanProps> = ({
  schoolHash,
  grade,
  classroomData,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [showGroupSection, setShowGroupSection] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [selectedStories, setSelectedStories] = useState<string[]>([]);
  const [originallyAssignedStories, setOriginallyAssignedStories] = useState<
    string[]
  >([]);
  const [showStoriesSection, setShowStoriesSection] = useState(false);
  const [expandedStudentLists, setExpandedStudentLists] = useState<string[]>(
    [],
  );
  const [isAssigning, setIsAssigning] = useState(false);
  const [expandedGroupStudents, setExpandedGroupStudents] = useState<string[]>(
    [],
  );
  const [expandedStoryLists, setExpandedStoryLists] = useState<string[]>([]);
  const [showEditSection, setShowEditSection] = useState(false);

  const timeFilter = "This week";

  // Fetch groups data
  const {
    data: groupsData,
    loading: groupsLoading,
    refetch: refetchGroups,
  } = useGetGroups({
    schoolHash,
    grade,
    enabled: true,
  });

  // Fetch SEL stories data
  const { data: selStoriesData, loading: storiesLoading } = useSELStories({
    schoolHash,
    grade,
    enabled: true,
  });

  // Get all SEL categories (filter out Uncategorized)
  const selCategories = (selStoriesData?.data || []).filter(
    (cat) => cat.selCompetency.toLowerCase() !== "uncategorized",
  );

  // Set initial category when data loads
  useEffect(() => {
    if (selCategories.length > 0 && !selectedCategory) {
      setSelectedCategory(selCategories[0].selCompetency);
    }
  }, [selCategories, selectedCategory]);

  // Get current category stories
  const currentCategoryData = selCategories.find(
    (cat) => cat.selCompetency === selectedCategory,
  );
  const currentStories: SELStory[] = currentCategoryData?.stories || [];

  // Get groups for selection
  const customGroups = groupsData?.customGroups || [];
  const defaultGroup = groupsData?.defaultGroup;
  const allGroups = defaultGroup
    ? [defaultGroup, ...customGroups]
    : customGroups;

  const handleGroupSelect = (groupId: string) => {
    setSelectedGroup(groupId);

    // Load assigned stories for the selected group
    const selectedGroupData = allGroups.find((g) => g.id === groupId);
    if (selectedGroupData?.assignedStories) {
      const assignedStoryIds = selectedGroupData.assignedStories.map(
        (story) => story.id,
      );
      setSelectedStories(assignedStoryIds);
      setOriginallyAssignedStories(assignedStoryIds);
    } else {
      setSelectedStories([]);
      setOriginallyAssignedStories([]);
    }
  };

  const toggleStudentList = (groupId: string) => {
    const isExpanding = !expandedStudentLists.includes(groupId);
    if (isExpanding) {
      trackEvent("LEARNING_PLAN_SECTION_EXPANDED", {
        section: "Student List",
        groupId,
      });
    }
    setExpandedStudentLists((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  const handleStorySelect = (storyId: string) => {
    // Prevent unselecting already assigned stories
    if (originallyAssignedStories.includes(storyId)) {
      return;
    }

    setSelectedStories((prev) =>
      prev.includes(storyId)
        ? prev.filter((id) => id !== storyId)
        : [...prev, storyId],
    );
  };

  const toggleGroupStudents = (groupId: string) => {
    setExpandedGroupStudents((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  const toggleStoryList = (groupId: string) => {
    setExpandedStoryLists((prev) =>
      prev.includes(groupId)
        ? prev.filter((id) => id !== groupId)
        : [...prev, groupId],
    );
  };

  // Helper function to get story name by ID
  const getStoryNameById = (storyId: string): string => {
    for (const category of selCategories) {
      const story = category.stories.find((s) => s.documentId === storyId);
      if (story) {
        return story.name;
      }
    }
    return "";
  };

  // Helper function to get story name by ID
  const getStoryOrderById = (storyId: string): number | null => {
    for (const category of selCategories) {
      const story = category.stories.find((s) => s.documentId === storyId);
      if (story) {
        return story.order;
      }
    }
    return null;
  };

  const handleAssignStories = async () => {
    if (!selectedGroup) {
      toast.error("Please select a group");
      return;
    }

    const selectedGroupData = allGroups.find((g) => g.id === selectedGroup);
    if (!selectedGroupData) {
      toast.error("Selected group not found");
      return;
    }

    // Calculate newly added stories only
    const newlyAddedStories = selectedStories.filter(
      (id) => !originallyAssignedStories.includes(id),
    );

    if (newlyAddedStories.length === 0) {
      toast.info("No new stories to assign");
      return;
    }

    try {
      setIsAssigning(true);

      const requestBody = {
        schoolHash: schoolHash,
        classId: groupsData?.classId || "",
        name: selectedGroupData.name,
        type:
          selectedGroupData.id === groupsData?.defaultGroup?.id
            ? "default"
            : "custom",
        stories: newlyAddedStories.map((storyId) => ({
          id: storyId,
          order: getStoryOrderById(storyId),
        })),
      };

      // Call PUT API for newly added stories
      await apiClient.put(apiPaths.ASSIGN_STORIES, requestBody);

      // Track story assignment
      trackEvent("STORY_ASSIGNED", {
        groupId: selectedGroup,
        groupType:
          selectedGroupData.id === groupsData?.defaultGroup?.id
            ? "default"
            : "custom",
        storiesCount: newlyAddedStories.length,
        grade,
      });

      // Refetch groups data to get updated assignedStories
      await refetchGroups();

      // Update originally assigned stories to current selection
      setOriginallyAssignedStories(selectedStories);

      toast.success(
        `${newlyAddedStories.length} ${newlyAddedStories.length > 1 ? "stories" : "story"} assigned`,
      );
    } catch (error: any) {
      console.error("Error assigning stories:", error);
      toast.error(
        error?.response?.data?.message ||
          "Failed to assign stories. Please try again.",
      );
    } finally {
      setIsAssigning(false);
    }
  };

  return (
    <div className="learning-plan-overview">
      {/* All Learning Plans Section */}
      {allGroups.length > 0 && (
        <div className="all-learning-plans-section">
          <div className="all-plans-header">
            <h2>Assigned Stories</h2>
            <button
              className="new-plan-btn"
              onClick={() => {
                setShowEditSection(true);
                setShowGroupSection(true);
                setShowStoriesSection(false);
                setSelectedGroup(null);
                setSelectedStories([]);
                setOriginallyAssignedStories([]);
                // Scroll to edit section
                setTimeout(() => {
                  document
                    .querySelector(".edit-learning-plan-section")
                    ?.scrollIntoView({ behavior: "smooth" });
                }, 100);
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="#9747FF"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
              <span>Assign a Story</span>
            </button>
          </div>

          <div className="learning-plans-list">
            {allGroups.map((group) => (
              <div key={group.id} className="learning-plan-card">
                <div className="plan-header">
                  <h3 className="group-name">{group.name}</h3>
                  <button
                    className="student-list-link"
                    onClick={() => toggleGroupStudents(group.id)}
                  >
                    {group.students?.length || 0} students
                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <path
                        d="M4 6L8 10L12 6"
                        stroke="#9747FF"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        style={{
                          transform: expandedGroupStudents.includes(group.id)
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                          transformOrigin: "center",
                          transition: "transform 0.3s ease",
                        }}
                      />
                    </svg>
                  </button>
                  {expandedGroupStudents.includes(group.id) &&
                    group.students &&
                    group.students.length > 0 && (
                      <div className="student-list-expanded">
                        {group.students.map((student: any, index: number) => (
                          <span
                            key={student.id || index}
                            className="student-name"
                          >
                            {student.name}
                          </span>
                        ))}
                      </div>
                    )}
                </div>
                <div className="assigned-stories">
                  {(() => {
                    if (
                      !group.assignedStories ||
                      group.assignedStories.length === 0
                    ) {
                      return (
                        <span className="no-stories">No stories assigned</span>
                      );
                    }

                    const PILLS_PER_ROW = 5;
                    const INITIAL_ROWS = 1;
                    const COLLAPSE_LIMIT = PILLS_PER_ROW * INITIAL_ROWS;
                    const hasMore =
                      group.assignedStories.length > COLLAPSE_LIMIT;
                    const isExpanded = expandedStoryLists.includes(group.id);
                    const storiesToShow =
                      hasMore && !isExpanded
                        ? group.assignedStories.slice(0, COLLAPSE_LIMIT)
                        : group.assignedStories;

                    return (
                      <>
                        <div className="story-pills">
                          {storiesToShow.map((story) => (
                            <span key={story.id} className="story-pill">
                              {getStoryNameById(story.id)}
                            </span>
                          ))}
                        </div>
                        {hasMore && (
                          <button
                            className="view-more-btn"
                            onClick={() => toggleStoryList(group.id)}
                          >
                            {isExpanded ? "View less" : "View more"}
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Edit Learning Plan Section */}
      {showEditSection && (
        <div className="edit-learning-plan-section">
          <div className="section-header-with-close">
            <h2 className="section-title">Assign a Story</h2>
            <button
              className="close-btn"
              onClick={() => {
                setShowEditSection(false);
                setSelectedGroup(null);
                setSelectedStories([]);
                setOriginallyAssignedStories([]);
                setShowGroupSection(true);
                setShowStoriesSection(false);
              }}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <path
                  d="M18 6L6 18M6 6l12 12"
                  stroke="#9A8E8E"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="edit-content">
            {/* Choose Group Section */}
            <div
              className={`choose-group-section ${!showGroupSection && selectedGroup ? "collapsed" : ""}`}
            >
              {showGroupSection ? (
                <div className="section-header-with-number">
                  <div className="header-left">
                    <span className="step-number">1.</span>
                    <span className="step-title">Choose the group</span>
                  </div>
                  <button
                    className="toggle-section-btn"
                    onClick={() => setShowGroupSection(!showGroupSection)}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <line
                        x1="6"
                        y1="12"
                        x2="18"
                        y2="12"
                        stroke="#9A8E8E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              ) : (
                <div className="section-header-collapsed">
                  <div className="header-left">
                    <span className="step-number">1.</span>
                    <span className="step-title">Choose the group</span>
                  </div>
                  <button
                    className="toggle-section-btn"
                    onClick={() => setShowGroupSection(!showGroupSection)}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <line
                        x1="12"
                        y1="6"
                        x2="12"
                        y2="18"
                        stroke="#9A8E8E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                      <line
                        x1="6"
                        y1="12"
                        x2="18"
                        y2="12"
                        stroke="#9A8E8E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    </svg>
                  </button>
                </div>
              )}

              {!showGroupSection && selectedGroup && (
                <div className="selected-group-summary">
                  <h3>{allGroups.find((g) => g.id === selectedGroup)?.name}</h3>
                </div>
              )}

              {showGroupSection && (
                <>
                  <div className="group-list">
                    {allGroups.map((group) => {
                      const hasStudents =
                        group.students && group.students.length > 0;
                      return (
                        <div
                          key={group.id}
                          className={`group-item ${!hasStudents ? "disabled" : ""}`}
                        >
                          <div className="group-row">
                            <button
                              className={`radio-button ${selectedGroup === group.id ? "selected" : ""}`}
                              onClick={() =>
                                hasStudents && handleGroupSelect(group.id)
                              }
                              disabled={!hasStudents}
                            >
                              {selectedGroup === group.id && (
                                <svg
                                  width="20"
                                  height="20"
                                  viewBox="0 0 20 20"
                                  fill="none"
                                >
                                  <path
                                    d="M4 10L8 14L16 6"
                                    stroke="white"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </svg>
                              )}
                            </button>
                            <div className="group-info">
                              <span className="group-name">{group.name}</span>
                              <button
                                className="student-list-toggle"
                                onClick={() => toggleStudentList(group.id)}
                              >
                                Student list
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 16 16"
                                  fill="none"
                                >
                                  <path
                                    d="M4 6L8 10L12 6"
                                    stroke="#9747FF"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    style={{
                                      transform: expandedStudentLists.includes(
                                        group.id,
                                      )
                                        ? "rotate(180deg)"
                                        : "rotate(0deg)",
                                      transformOrigin: "center",
                                    }}
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                          {expandedStudentLists.includes(group.id) && (
                            <div className="student-list-content">
                              {group.students && group.students.length > 0 ? (
                                group.students.map((student: any) => (
                                  <div
                                    key={student.id}
                                    className="student-item-in-list"
                                  >
                                    {student.name}
                                  </div>
                                ))
                              ) : (
                                <div className="no-students">
                                  No students in this group
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <button
                    className="next-btn"
                    onClick={() => {
                      setShowGroupSection(false);
                      setShowStoriesSection(true);
                    }}
                    disabled={!selectedGroup}
                  >
                    Next
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="32"
                      height="32"
                      viewBox="0 0 32 32"
                      fill="none"
                    >
                      <g clip-path="url(#clip0_3925_4878)">
                        <path
                          d="M1.92338 13.754C2.05431 13.7327 2.18682 13.7229 2.3194 13.7246H24.8948L24.4025 13.4957C23.9213 13.2679 23.4836 12.958 23.1089 12.5798L16.7782 6.24913C15.9444 5.45321 15.8043 4.17283 16.4462 3.21543C17.1932 2.19521 18.6259 1.97369 19.6462 2.72074C19.7286 2.78113 19.807 2.84695 19.8806 2.91778L31.3285 14.3657C32.2231 15.2594 32.2239 16.709 31.3303 17.6037C31.3297 17.6042 31.3291 17.6049 31.3285 17.6055L19.8806 29.0534C18.9852 29.9462 17.5355 29.9442 16.6426 29.0489C16.5723 28.9784 16.5067 28.9034 16.4462 28.8244C15.8043 27.867 15.9444 26.5866 16.7782 25.7907L23.0974 19.4486C23.4334 19.1123 23.8196 18.8303 24.2422 18.6129L24.9291 18.3038H2.4454C1.27578 18.3472 0.249619 17.5302 0.0298901 16.3805C-0.172523 15.1324 0.675198 13.9564 1.92338 13.754Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_3925_4878">
                          <rect
                            width="32"
                            height="32"
                            fill="white"
                            transform="matrix(-1 0 0 1 32 0)"
                          />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                </>
              )}
            </div>

            {/* Choose Stories Section */}
            <div className="choose-stories-section">
              <div className="section-header-with-number">
                <div className="header-left">
                  <span className="step-number">2.</span>
                  <span className="step-title">Choose stories to assign</span>
                </div>
                <button
                  className="toggle-section-btn"
                  onClick={() => setShowStoriesSection(!showStoriesSection)}
                  disabled={!selectedGroup}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                    {showStoriesSection ? (
                      <line
                        x1="6"
                        y1="12"
                        x2="18"
                        y2="12"
                        stroke="#9A8E8E"
                        strokeWidth="2"
                        strokeLinecap="round"
                      />
                    ) : (
                      <>
                        <line
                          x1="12"
                          y1="6"
                          x2="12"
                          y2="18"
                          stroke="#9A8E8E"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                        <line
                          x1="6"
                          y1="12"
                          x2="18"
                          y2="12"
                          stroke="#9A8E8E"
                          strokeWidth="2"
                          strokeLinecap="round"
                        />
                      </>
                    )}
                  </svg>
                </button>
              </div>

              {showStoriesSection && selectedGroup && (
                <div className="stories-content">
                  {/* SEL Category Tabs */}
                  <div className="category-tabs">
                    {selCategories.map((category) => (
                      <button
                        key={category.selCompetency}
                        className={`category-tab ${selectedCategory === category.selCompetency ? "selected" : ""}`}
                        onClick={() =>
                          setSelectedCategory(category.selCompetency)
                        }
                      >
                        {category.selCompetency}
                      </button>
                    ))}
                  </div>

                  {/* Stories Table */}
                  <div className="stories-table">
                    <div className="table-header">
                      <div className="col-story-name">Story name</div>
                      <div className="col-themes">Themes</div>
                      <div className="col-skills">Focus Skills</div>
                      <div className="col-age">Age group</div>
                      <div className="col-time">Estimated time</div>
                      <div className="col-select">Select</div>
                    </div>

                    <div className="table-body">
                      {storiesLoading ? (
                        <div className="loading-message">
                          Loading stories...
                        </div>
                      ) : currentStories.length === 0 ? (
                        <div className="no-stories-message">
                          No stories available for this category
                        </div>
                      ) : (
                        currentStories.map((story) => {
                          const isAlreadyAssigned =
                            originallyAssignedStories.includes(
                              story.documentId,
                            );
                          return (
                            <div key={story.documentId} className="table-row">
                              <div className="col-story-name">
                                <div className="story-info">
                                  <span className="story-title">
                                    {story.name}
                                  </span>
                                  {story.plot && (
                                    <span className="story-description">
                                      {story.plot}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-themes">
                                {story.theme && (
                                  <span className="tag theme-tag">
                                    {story.theme}
                                  </span>
                                )}
                              </div>
                              <div className="col-skills">
                                {story.focusSkills && (
                                  <span className="tag skill-tag">
                                    {story.focusSkills}
                                  </span>
                                )}
                              </div>
                              <div className="col-age">
                                <span className="tag age-tag">
                                  {story.ageGroup}
                                </span>
                              </div>
                              <div className="col-time">
                                <span>{story.estimatedTime}</span>
                              </div>
                              <div className="col-select">
                                <button
                                  className={`select-checkbox ${selectedStories.includes(story.documentId) ? "checked" : ""} ${isAlreadyAssigned ? "disabled" : ""}`}
                                  onClick={() =>
                                    handleStorySelect(story.documentId)
                                  }
                                  disabled={isAlreadyAssigned}
                                >
                                  {selectedStories.includes(
                                    story.documentId,
                                  ) && (
                                    <svg
                                      width="24"
                                      height="24"
                                      viewBox="0 0 24 24"
                                      fill="none"
                                    >
                                      <path
                                        d="M5 12L10 17L20 7"
                                        stroke="white"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                      />
                                    </svg>
                                  )}
                                </button>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Assign Button */}
          {selectedGroup &&
            selectedStories.filter(
              (id) => !originallyAssignedStories.includes(id),
            ).length > 0 && (
              <button
                className="assign-btn"
                onClick={handleAssignStories}
                disabled={isAssigning}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M12 5v14M5 12h14"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
                <span>
                  {isAssigning
                    ? "Assigning..."
                    : "Assign stories to selected groups"}
                </span>
              </button>
            )}
        </div>
      )}
    </div>
  );
};

export default LearningPlan;
